import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiVideo, FiEdit3, FiUpload, FiCheck, FiGift, FiImage, FiX } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const SubmitTestimonial = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  // Multi-action form state
  const [textContent, setTextContent] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Check if user has started any action
  const hasAnyContent = () => {
    return videoFile !== null || textContent.trim().length > 0 || imageFile !== null;
  };

  // Calculate completed actions for reward
  const getActionsCount = () => {
    let count = 0;
    if (videoFile) count++;
    if (textContent.trim().length >= 50) count++;
    if (imageFile) count++;
    return count;
  };

  const getRewardDays = () => {
    const actions = getActionsCount();
    if (actions === 3) return 60; // Video + Text + Screenshot = 2 months
    if (actions >= 1) return 30; // 1 or 2 actions = 1 month
    return 0;
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        setError('Video file must be less than 100MB');
        return;
      }
      if (!file.type.startsWith('video/')) {
        setError('Please upload a video file');
        return;
      }
      setVideoFile(file);
      setError('');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file must be less than 10MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }
      setImageFile(file);
      setError('');
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeVideo = () => {
    setVideoFile(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Validate that at least one complete action is done
    if (!hasAnyContent()) {
      setError('Please complete at least one action: upload a video, write a review (50+ characters), or add a screenshot');
      return;
    }

    // Check if text is started but not complete
    if (textContent.trim().length > 0 && textContent.trim().length < 50 && !videoFile && !imageFile) {
      setError(`Your review must contain at least 50 characters (currently ${textContent.trim().length}/50). Add more text or upload a video/screenshot.`);
      return;
    }

    if (textContent.trim().length > 0 && textContent.trim().length < 50) {
      setError(`Your written review must contain at least 50 characters (${textContent.trim().length}/50)`);
      return;
    }

    setUploading(true);
    setError('');

    try {
      let videoUrl = null;
      let screenshotUrl = null;

      // Upload video if provided
      if (videoFile) {
        const fileExt = videoFile.name.split('.').pop();
        const fileName = `${user.id}/video_${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('testimonials')
          .upload(fileName, videoFile, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          throw new Error('Failed to upload video: ' + uploadError.message);
        }

        const { data: publicUrl } = supabase.storage
          .from('testimonials')
          .getPublicUrl(fileName);
        
        videoUrl = publicUrl.publicUrl;
      }

      // Upload screenshot if provided
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${user.id}/screenshot_${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('testimonials')
          .upload(fileName, imageFile, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          throw new Error('Failed to upload screenshot: ' + uploadError.message);
        }

        const { data: publicUrl } = supabase.storage
          .from('testimonials')
          .getPublicUrl(fileName);
        
        screenshotUrl = publicUrl.publicUrl;
      }

      // Get user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, name, email')
        .eq('user_id', user.id)
        .maybeSingle();

      const userName = profile?.first_name 
        ? `${profile.first_name} ${profile.last_name || ''}`.trim()
        : profile?.name || 'Anonymous';

      // Determine type based on what was provided
      const hasVideo = !!videoUrl;
      const hasText = textContent.trim().length >= 50;
      const type = hasVideo ? 'video' : 'text';

      // Create testimonial record
      const { error: insertError } = await supabase
        .from('testimonials')
        .insert({
          user_id: user.id,
          user_email: user.email || profile?.email || '',
          user_name: userName,
          type,
          text_content: hasText ? textContent : null,
          video_url: videoUrl,
          screenshot_url: screenshotUrl,
          reward_days: getRewardDays(),
          status: 'pending',
        });

      if (insertError) {
        throw new Error('Failed to submit testimonial: ' + insertError.message);
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.message || 'Failed to submit testimonial');
    } finally {
      setUploading(false);
    }
  };

  // Not logged in state
  if (!user) {
    return (
      <div className="container mx-auto px-4 pt-32 pb-20 text-center">
        <h1 className="text-3xl font-bold mb-4 text-white">Please Log In</h1>
        <p className="text-gray-400 mb-8">You need to be logged in to submit a testimonial.</p>
        <button
          onClick={() => navigate('/login')}
          className="px-8 h-12 bg-gray-900/80 hover:bg-gray-900 text-white font-medium rounded-lg transition-all duration-200 border border-purple-500/20 hover:border-purple-500/40"
        >
          Log In
        </button>
      </div>
    );
  }

  // Success state
  if (submitted) {
    const rewardMonths = getRewardDays() === 60 ? 2 : 1;
    return (
      <div className="container mx-auto px-4 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center">
            <FiCheck className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-white">Thank You! 🎉</h1>
          <p className="text-gray-400 mb-6">
            Your testimonial has been submitted successfully! Our team will review it shortly.
          </p>
          <div className="p-4 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-xl mb-8">
            <p className="text-cyan-400 font-semibold">
              🎁 {rewardMonths} month{rewardMonths > 1 ? 's' : ''} of FREE Institutional AI access
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Will be added to your account once approved
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 h-12 bg-gray-900/80 hover:bg-gray-900 text-white font-medium rounded-lg transition-all duration-200 border border-purple-500/20 hover:border-purple-500/40"
          >
            Back to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  const actionsCount = getActionsCount();
  const rewardDays = getRewardDays();

  return (
    <div className="container mx-auto px-4 pt-32 pb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
            <FiGift className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-3xl font-bold mb-2 text-white">
            Share Your <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Success Story</span>
          </h1>
          <p className="text-gray-400">
            Get FREE Institutional AI access in exchange for your testimonial
          </p>
        </div>

        {/* Rewards Preview */}
        <div className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-xl mb-8">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FiGift className="text-purple-400" />
            Reward System
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Complete ALL 3 actions (Video + Text + Screenshot)</span>
              <span className="font-bold text-cyan-400">2 Months FREE</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Complete 1 or 2 actions</span>
              <span className="font-bold text-cyan-400">1 Month FREE</span>
            </div>
          </div>
          {actionsCount > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <span className="text-white font-semibold">Your current reward:</span>
                <span className="text-lg font-bold text-green-400">
                  {rewardDays === 60 ? '2 Months' : '1 Month'} FREE
                  <span className="text-sm text-gray-400 ml-2">({actionsCount}/3 actions)</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Form Sections */}
        <div className="space-y-6">
          {/* 1. Video Upload */}
          <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-white">
              <FiVideo className="text-purple-400" />
              1. Video Testimonial
              {videoFile && <FiCheck className="text-green-400 ml-auto" />}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Record a 1-3 minute video sharing your experience with SnapChart AI
            </p>
            
            {videoFile ? (
              <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div>
                  <p className="text-white font-medium">{videoFile.name}</p>
                  <p className="text-gray-400 text-sm">{(videoFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                </div>
                <button onClick={removeVideo} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <FiX className="text-red-400" />
                </button>
              </div>
            ) : (
              <div 
                onClick={() => videoInputRef.current?.click()}
                className="border-2 border-dashed border-purple-500/30 rounded-xl p-6 text-center cursor-pointer hover:border-purple-500/50 transition-all"
              >
                <FiUpload className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                <p className="text-gray-300">Click to upload video</p>
                <p className="text-gray-500 text-sm">MP4, MOV, WebM (max 100MB)</p>
              </div>
            )}
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={handleVideoSelect}
              className="hidden"
            />
          </div>

          {/* 2. Written Review */}
          <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-white">
              <FiEdit3 className="text-cyan-400" />
              2. Written Review
              {textContent.trim().length >= 50 && <FiCheck className="text-green-400 ml-auto" />}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Write about how SnapChart AI has helped improve your trading
            </p>
            
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="I've been using SnapChart AI for..."
              rows={5}
              className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-transparent resize-none"
            />
            <p className={`text-sm mt-2 ${textContent.length >= 50 ? 'text-green-400' : 'text-gray-500'}`}>
              {textContent.length}/50 characters minimum
            </p>
          </div>

          {/* 3. Screenshot */}
          <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-white">
              <FiImage className="text-yellow-400" />
              3. Trading Screenshot
              {imageFile && <FiCheck className="text-green-400 ml-auto" />}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Share a trading chart or screenshot showing your results with SnapChart AI
            </p>

            {imagePreview ? (
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Screenshot preview" 
                  className="w-full max-h-48 object-contain rounded-xl border border-white/10"
                />
                <button
                  onClick={removeImage}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                >
                  <FiX className="w-5 h-5 text-white" />
                </button>
              </div>
            ) : (
              <div 
                onClick={() => imageInputRef.current?.click()}
                className="border-2 border-dashed border-yellow-500/30 rounded-xl p-6 text-center cursor-pointer hover:border-yellow-500/50 transition-all"
              >
                <FiImage className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
                <p className="text-gray-300">Click to add a screenshot</p>
                <p className="text-gray-500 text-sm">PNG, JPG, WebP (max 10MB)</p>
              </div>
            )}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={uploading}
            className="w-full h-12 bg-black hover:bg-black/90 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 border border-white/10 shadow-lg"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="tracking-wide">Uploading...</span>
              </>
            ) : (
              <>
                <span className="tracking-wide">Submit Testimonial</span>
                {rewardDays > 0 && (
                  <span className="px-2 py-0.5 bg-white/10 border border-white/20 rounded text-xs font-normal">
                    +{rewardDays === 60 ? '2 months' : '1 month'} free
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default SubmitTestimonial;
